import { Component, type ErrorInfo, type ReactNode } from 'react';

interface ErrorBoundaryProps {
  children: ReactNode;
  /** Optional fallback UI; defaults to a simple reload screen. */
  fallback?: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

/**
 * Top-level error boundary. Catches render-time errors anywhere in the tree
 * and shows a recoverable fallback instead of a blank screen.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, info: ErrorInfo): void {
    // Intentionally use console.error here — production builds strip console
    // calls via the Vite esbuild config, so this is a no-op in prod.
    console.error('[ErrorBoundary]', error, info.componentStack);
  }

  private handleReload = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (!this.state.hasError) return this.props.children;
    if (this.props.fallback) return this.props.fallback;
    return (
      <div role="alert" style={{
        padding: 32,
        maxWidth: 560,
        margin: '64px auto',
        border: '1px solid #e0c0c0',
        background: '#fff5f5',
        borderRadius: 8,
        fontFamily: 'Arial, Helvetica, sans-serif',
      }}>
        <h2 style={{ marginTop: 0, color: '#a00' }}>Es ist ein Fehler aufgetreten.</h2>
        <p>Bitte laden Sie die Seite neu. Falls das Problem bestehen bleibt, melden Sie sich bitte beim Veranstaltungsteam.</p>
        <button
          type="button"
          onClick={this.handleReload}
          style={{
            padding: '10px 20px',
            background: '#007bff',
            color: '#fff',
            border: 'none',
            borderRadius: 5,
            cursor: 'pointer',
            fontSize: 16,
          }}
        >
          Seite neu laden
        </button>
      </div>
    );
  }
}

export default ErrorBoundary;
